import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  BarChart3, Award, Trophy, TrendingUp, Target, Calendar,
  DollarSign, CheckCircle, Clock, Star, User
} from "lucide-react";

export default function MyPerformance({ user }) {
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(false);
  
  // Period selection
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  
  // Data
  const [summary, setSummary] = useState(null);
  const [awards, setAwards] = useState(null);
  const [targets, setTargets] = useState(null);
  
  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 2 + i);
  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];

  useEffect(() => {
    fetchSummary();
    fetchAwards();
  }, [selectedYear]);

  useEffect(() => {
    fetchTargets();
  }, [selectedYear, selectedMonth]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/my-performance/summary?period_year=${selectedYear}`);
      setSummary(res.data);
    } catch (error) {
      if (error.response?.status !== 400) {
        toast.error("Failed to load performance summary");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAwards = async () => {
    try {
      const res = await api.get(`/my-performance/awards?period_year=${selectedYear}`);
      setAwards(res.data);
    } catch (error) {
      console.error("Failed to load awards");
    }
  };

  const fetchTargets = async () => {
    try {
      const res = await api.get(`/my-performance/targets?period_year=${selectedYear}&period_month=${selectedMonth}`);
      setTargets(res.data);
    } catch (error) {
      console.error("Failed to load targets");
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+': return 'bg-emerald-500 text-white';
      case 'A': return 'bg-green-500 text-white';
      case 'B+': return 'bg-lime-500 text-white';
      case 'B': return 'bg-yellow-500 text-white';
      case 'C': return 'bg-orange-500 text-white';
      case 'D': return 'bg-red-400 text-white';
      case 'F': return 'bg-red-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 100) return 'text-emerald-600';
    if (score >= 80) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAchievementColor = (percent) => {
    if (percent >= 100) return 'bg-emerald-500';
    if (percent >= 80) return 'bg-green-500';
    if (percent >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500 text-white"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'paid':
        return <Badge className="bg-blue-500 text-white"><DollarSign className="h-3 w-3 mr-1" />Paid</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="border-gray-500 text-gray-500">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Check if user has employee_id linked
  const hasEmployeeId = user?.employee_id;

  if (!hasEmployeeId) {
    return (
      <Layout user={user} title="My Performance">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <User className="h-16 w-16 text-md3-outline mb-4" />
          <h2 className="text-md3-headline-small text-md3-on-surface mb-2">Employee Profile Not Linked</h2>
          <p className="text-md3-body-medium text-md3-on-surface-variant max-w-md">
            Your user account is not linked to an employee record. Please contact HR to link your employee profile to view your performance data.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} title="My Performance">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-headline-medium text-md3-on-surface">My Performance</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant">
              View your performance metrics, targets, and awards
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-36 bg-md3-surface border-md3-outline rounded-md3-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-28 bg-md3-surface border-md3-outline rounded-md3-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-md3-surface-container rounded-md3-full p-1">
            <TabsTrigger value="summary" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <BarChart3 className="h-4 w-4 mr-2" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="targets" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Target className="h-4 w-4 mr-2" />
              Monthly Targets
            </TabsTrigger>
            <TabsTrigger value="awards" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Trophy className="h-4 w-4 mr-2" />
              My Awards
            </TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-6 mt-6">
            {summary && (
              <>
                {/* Current Assignment Card */}
                {summary.current_assignment && (
                  <Card className="bg-gradient-to-r from-md3-primary-container to-md3-secondary-container border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-primary-container/70">Current KRA Assignment</p>
                          <p className="text-md3-headline-small text-md3-on-primary-container">
                            {summary.current_assignment.kra_type_name}
                          </p>
                          <p className="text-md3-body-medium text-md3-on-primary-container/80 mt-1">
                            Effective from: {summary.current_assignment.effective_from}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-md3-body-small text-md3-on-primary-container/70">YTD Average Score</p>
                          <p className={`text-md3-display-small ${getScoreColor(summary.ytd_average_score)}`}>
                            {summary.ytd_average_score}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Performance Summaries */}
                <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                  <CardHeader>
                    <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Performance History
                    </CardTitle>
                    <CardDescription>Your monthly performance summaries</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {summary.recent_summaries?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Period</TableHead>
                            <TableHead>Score</TableHead>
                            <TableHead>Grade</TableHead>
                            <TableHead>Earned Weightage</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {summary.recent_summaries.map((s, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">
                                {months.find(m => m.value === s.period_month)?.label} {s.period_year}
                              </TableCell>
                              <TableCell className={`font-medium ${getScoreColor(s.overall_score)}`}>
                                {s.overall_score}%
                              </TableCell>
                              <TableCell>
                                <Badge className={getGradeColor(s.performance_grade)}>
                                  {s.performance_grade}
                                </Badge>
                              </TableCell>
                              <TableCell>{s.total_earned_weightage}%</TableCell>
                              <TableCell>
                                {s.status === 'approved' ? (
                                  <Badge className="bg-emerald-100 text-emerald-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Approved
                                  </Badge>
                                ) : s.status === 'submitted' ? (
                                  <Badge className="bg-blue-100 text-blue-800">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Under Review
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">Draft</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                        <BarChart3 className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                        <p>No performance summaries yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Monthly Targets Tab */}
          <TabsContent value="targets" className="space-y-4 mt-6">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface">
                  {months.find(m => m.value === selectedMonth)?.label} {selectedYear} Targets
                </CardTitle>
                <CardDescription>Your targets and current achievement</CardDescription>
              </CardHeader>
              <CardContent>
                {targets?.targets?.length > 0 ? (
                  <div className="space-y-4">
                    {targets.targets.map((target, idx) => (
                      <div key={idx} className="p-4 bg-md3-surface-container rounded-md3-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-md3-body-large text-md3-on-surface font-medium">
                              {target.kra_line_item_name}
                            </p>
                            <p className="text-md3-body-small text-md3-on-surface-variant">
                              Weightage: {target.weightage || 0}%
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-md3-headline-small ${getScoreColor(target.achievement_percent)}`}>
                              {target.achievement_percent}%
                            </p>
                            <p className="text-md3-body-small text-md3-on-surface-variant">Achievement</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-md3-body-small">
                            <span>Target: {target.target_value} {target.target_unit}</span>
                            <span>Actual: {target.actual_value} {target.target_unit}</span>
                          </div>
                          <Progress 
                            value={Math.min(target.achievement_percent, 100)} 
                            className={`h-2 ${getAchievementColor(target.achievement_percent)}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                    <Target className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                    <p>No targets set for this period</p>
                    <p className="text-md3-body-small mt-1">
                      {targets?.message || "Contact your manager for target assignment"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Awards Tab */}
          <TabsContent value="awards" className="space-y-6 mt-6">
            {awards && (
              <>
                {/* Awards Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Total Awards</p>
                          <p className="text-md3-headline-medium text-md3-on-surface">{awards.total_awards}</p>
                        </div>
                        <Trophy className="h-10 w-10 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Total Bonus</p>
                          <p className="text-md3-headline-medium text-emerald-600">{formatCurrency(awards.total_bonus)}</p>
                        </div>
                        <DollarSign className="h-10 w-10 text-emerald-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Paid</p>
                          <p className="text-md3-headline-medium text-blue-600">{formatCurrency(awards.paid_bonus)}</p>
                        </div>
                        <CheckCircle className="h-10 w-10 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Pending</p>
                          <p className="text-md3-headline-medium text-amber-600">{formatCurrency(awards.pending_bonus)}</p>
                        </div>
                        <Clock className="h-10 w-10 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Awards List */}
                <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                  <CardHeader>
                    <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                      <Award className="h-5 w-5" />
                      Award History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {awards.awards?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Award Type</TableHead>
                            <TableHead>Period</TableHead>
                            <TableHead>Bonus</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Approved By</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {awards.awards.map((award, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  <Star className="h-4 w-4 text-amber-500" />
                                  {award.award_type_name}
                                </div>
                              </TableCell>
                              <TableCell>
                                {award.period_month ? `${months.find(m => m.value === award.period_month)?.label} ` : ''}
                                {award.period_year}
                              </TableCell>
                              <TableCell className="text-emerald-600 font-medium">
                                {formatCurrency(award.bonus_amount)}
                              </TableCell>
                              <TableCell>{getStatusBadge(award.bonus_status)}</TableCell>
                              <TableCell className="text-md3-on-surface-variant">
                                {award.approved_by_name}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                        <Trophy className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                        <p>No awards received yet</p>
                        <p className="text-md3-body-small mt-1">Keep up the good work!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
